import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError, endpoints, type FanSegment } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const PURPLE = "#c57eff";

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
    <path d="M10 7H4M4 7l3-3M4 7l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function FanSegments() {
  const { active } = useAuth();
  const segment = useMutation<{ segments: FanSegment[] }, Error, void>({
    mutationFn: () => endpoints.fanSegments(active!.slug),
  });

  const err = segment.error instanceof ApiError ? segment.error.message : segment.error?.message;

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>Fan segments</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            AI groups your fans by behaviour and country, with a promo angle for each.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/fans">
            <Button variant="ghost" size="sm"><ArrowLeft /> <span className="ml-2">Back to Fans</span></Button>
          </Link>
          <Button onClick={() => segment.mutate()} disabled={segment.isPending}>
            {segment.isPending ? "Segmenting…" : segment.data ? "Re-segment" : "Segment fans"}
          </Button>
        </div>
      </div>

      {err && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{err}</p>
          </CardContent>
        </Card>
      )}

      {!segment.data && !segment.isPending && !err && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-sm text-center py-12">
              Hit <span className="text-foreground">Segment fans</span> to ask the AI to find natural groups in your CRM.
            </p>
          </CardContent>
        </Card>
      )}

      {segment.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {segment.data.segments.map((s, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{s.name}</CardTitle>
                    <CardDescription>~{s.size_estimate} fans</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="text-foreground uppercase mb-1.5" style={{ fontSize: "11px", letterSpacing: "0.05em" }}>Criteria</h3>
                  <p className="text-sm" style={{ color: "#adaaaa" }}>{s.criteria}</p>
                </div>
                <div>
                  <h3 className="text-foreground uppercase mb-1.5" style={{ fontSize: "11px", letterSpacing: "0.05em" }}>Recommended angle</h3>
                  <p className="text-sm" style={{ color: PURPLE }}>{s.angle}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
